import { Box, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import React from 'react';
import AssetImage from '../../AssetImage';
import type BackendService from '../../../services/admin/BackendService';
import type { DocumentAsset } from '../../../services/admin/adminBackendServiceDocumentLibraryMixin';
import type { Box as CropBox, BoxHandle, SnapGuides } from './cropAdjustBoxes';
import SnapGuideLines from './SnapGuideLines';

export const PRIMARY_MAIN = 'primary.main';

const HANDLES: Exclude<BoxHandle, 'move'>[] = ['nw', 'ne', 'sw', 'se'];
const cornerOffsetSx: Record<Exclude<BoxHandle, 'move'>, object> = {
  nw: { top: -6, left: -6, cursor: 'nw-resize' },
  ne: { top: -6, right: -6, cursor: 'ne-resize' },
  sw: { bottom: -6, left: -6, cursor: 'sw-resize' },
  se: { bottom: -6, right: -6, cursor: 'se-resize' },
};

export function SelectionBox({
  box,
  scale,
  begin,
}: {
  box: CropBox;
  scale: number;
  begin: (index: number, handle: BoxHandle) => (event: React.PointerEvent) => void;
}) {
  return (
    <Box
      onPointerDown={begin(0, 'move')}
      sx={{
        position: 'absolute',
        cursor: 'move',
        border: '2px solid',
        borderColor: PRIMARY_MAIN,
        bgcolor: 'rgba(25,118,210,0.12)',
        boxSizing: 'border-box',
        left: box.left * scale,
        top: box.top * scale,
        width: box.width * scale,
        height: box.height * scale,
      }}
    >
      {HANDLES.map((h) => (
        <Box
          key={h}
          onPointerDown={begin(0, h)}
          sx={{
            position: 'absolute',
            width: 12,
            height: 12,
            bgcolor: PRIMARY_MAIN,
            ...cornerOffsetSx[h],
          }}
        />
      ))}
    </Box>
  );
}

export function DocumentPageThumbStrip({
  pageCount,
  currentPage,
  rangeFrom,
  rangeTo,
  onSelectPage,
}: {
  pageCount: number;
  currentPage: number;
  rangeFrom?: number;
  rangeTo?: number;
  documentAsset: DocumentAsset;
  backendService: BackendService;
  onSelectPage: (n: number) => void;
}) {
  if (pageCount <= 0) return null;
  return (
    <Box
      sx={{
        width: 80,
        flexShrink: 0,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        borderRight: '1px solid',
        borderColor: 'divider',
        pr: 0.5,
      }}
    >
      {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
        <Box
          key={n}
          onClick={() => onSelectPage(n)}
          aria-label={`Page ${n}`}
          sx={{
            cursor: 'pointer',
            aspectRatio: '16 / 9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
            border: '2px solid',
            borderColor:
              n === currentPage
                ? PRIMARY_MAIN
                : rangeFrom && rangeTo && n >= rangeFrom && n <= rangeTo
                  ? 'primary.light'
                  : 'transparent',
            borderRadius: 1,
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {n}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function CropModeTabs({
  mode,
  onModeChange,
}: {
  mode: 'image' | 'structure';
  onModeChange: (m: 'image' | 'structure') => void;
}) {
  return (
    <>
      <Tabs
        value={mode}
        onChange={(_, v) => onModeChange(v as 'image' | 'structure')}
        sx={{ mb: 0.5 }}
      >
        <Tab value="structure" label="Extract structured content" />
        <Tab value="image" label="Insert as image" />
      </Tabs>
      <Typography variant="caption" color="text.secondary">
        {mode === 'structure'
          ? 'Select a region to extract structured content (text, table, etc.) from the document.'
          : 'Select a region to insert it as an image into the block.'}
      </Typography>
    </>
  );
}

function CropPageStage({
  pageUrl,
  currentPage,
  wrapRef,
  imageDims,
  boxes,
  scale,
  guides,
  begin,
  onImageLoad,
  showCrop,
}: {
  pageUrl: string | null;
  currentPage: number;
  wrapRef: React.RefObject<HTMLDivElement>;
  imageDims: { width: number; height: number } | null;
  boxes: CropBox[];
  scale: number;
  guides: SnapGuides;
  begin: (index: number, handle: BoxHandle) => (event: React.PointerEvent) => void;
  onImageLoad: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  showCrop: boolean;
}) {
  if (!pageUrl) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  return (
    <Box
      ref={wrapRef}
      sx={{
        position: 'relative',
        flex: 1,
        overflowY: 'auto',
        lineHeight: 0,
        userSelect: 'none',
      }}
    >
      <AssetImage
        key={pageUrl}
        url={pageUrl}
        alt={`Page ${currentPage}`}
        sx={{ width: '100%', display: 'block' }}
        onLoad={onImageLoad}
      />
      {!imageDims && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.72)',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
      {showCrop && imageDims && boxes[0] && (
        <SelectionBox box={boxes[0]} scale={scale} begin={begin} />
      )}
      {showCrop && imageDims && <SnapGuideLines guides={guides} scale={scale} />}
    </Box>
  );
}

export function DocumentCropCanvas({
  mode,
  onModeChange,
  pageUrl,
  currentPage,
  wrapRef,
  imageDims,
  boxes,
  scale,
  guides,
  begin,
  onImageLoad,
  confirmError,
  showModeTabs = true,
  showCrop = true,
}: {
  mode: 'image' | 'structure';
  onModeChange: (m: 'image' | 'structure') => void;
  pageUrl: string | null;
  currentPage: number;
  wrapRef: React.RefObject<HTMLDivElement>;
  imageDims: { width: number; height: number } | null;
  boxes: CropBox[];
  scale: number;
  guides: SnapGuides;
  begin: (index: number, handle: BoxHandle) => (event: React.PointerEvent) => void;
  onImageLoad: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  confirmError: string | null;
  showModeTabs?: boolean;
  showCrop?: boolean;
}) {
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden' }}>
      {showModeTabs && <CropModeTabs mode={mode} onModeChange={onModeChange} />}
      {showCrop && (
        <Typography variant="caption" color="text.secondary">
          The selection snaps into place against the page edges. To move it straight up/down or
          straight left/right without it drifting, hold Shift while you drag it.
        </Typography>
      )}
      <CropPageStage
        pageUrl={pageUrl}
        currentPage={currentPage}
        wrapRef={wrapRef}
        imageDims={imageDims}
        boxes={boxes}
        scale={scale}
        guides={guides}
        begin={begin}
        onImageLoad={onImageLoad}
        showCrop={showCrop}
      />
      {confirmError && (
        <Typography variant="caption" color="error.main">
          {confirmError}
        </Typography>
      )}
    </Box>
  );
}
